import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Heart, Globe, Users, Shield, Target, Sparkles } from 'lucide-react';

const AboutPage = () => {
  const team = [
    {
      name: 'Anna Schmidt',
      role: 'Gründerin & CEO',
      image: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=300',
      bio: 'Reisebegeisterte und Hochzeitsplanerin mit 10 Jahren Erfahrung.',
    },
    {
      name: 'Thomas Weber',
      role: 'CTO',
      image: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=300',
      bio: 'Technologie-Experte mit Hintergrund in Marketplace-Plattformen.',
    },
    {
      name: 'Maria Garcia',
      role: 'Head of Community',
      image: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=300',
      bio: 'Kulturwissenschaftlerin und Expertin für interkulturelle Kommunikation.',
    },
  ];

  const values = [
    {
      icon: Heart,
      title: 'Authentizität',
      description: 'Wir glauben an echte Erlebnisse, nicht an inszenierte Events. Jede Hochzeit auf unserer Plattform ist eine reale Feier.',
    },
    {
      icon: Globe,
      title: 'Kulturelle Verbindung',
      description: 'Hochzeiten sind ein Fenster in die Seele einer Kultur. Wir bringen Menschen zusammen, um voneinander zu lernen.',
    },
    {
      icon: Shield,
      title: 'Vertrauen & Sicherheit',
      description: 'Die Sicherheit unserer Community hat höchste Priorität. Sichere Zahlungen und Datenschutz sind selbstverständlich.',
    },
    {
      icon: Users,
      title: 'Gemeinschaft',
      description: 'Wir bauen eine globale Gemeinschaft auf, die durch gemeinsame Feiern und unvergessliche Momente verbunden ist.',
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen" data-testid="about-page">
        {/* Hero Section */}
        <section className="relative py-20 bg-primary overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }} />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
              Über WEDVAYA
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Wir verbinden Menschen durch die schönsten Momente des Lebens. 
              WEDVAYA ist mehr als eine Plattform – es ist eine Bewegung für 
              kulturellen Austausch und echte menschliche Verbindungen.
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="flex items-center gap-2 text-primary mb-4">
                  <Target className="h-5 w-5" />
                  <span className="font-medium uppercase tracking-wider text-sm">Unsere Mission</span>
                </div>
                <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">
                  Kulturen verbinden, einen Moment nach dem anderen
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  Die Idee zu WEDVAYA entstand aus einer einfachen Beobachtung: 
                  Hochzeiten sind überall auf der Welt die schönsten und authentischsten 
                  kulturellen Feiern. Sie vereinen Musik, Tanz, Essen, Traditionen und 
                  vor allem – Menschen.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                  Doch als Tourist erlebt man selten diese intimen Momente. Man sieht 
                  Sehenswürdigkeiten, aber nicht das echte Leben. Gleichzeitig investieren 
                  Paare oft erhebliche Summen in ihre Hochzeit und würden sich über 
                  zusätzliche Unterstützung freuen.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  WEDVAYA bringt diese beiden Welten zusammen: Reisende erleben 
                  unvergessliche, authentische Momente, und Paare können ihre Kultur 
                  mit der Welt teilen – während sie einen Teil ihrer Kosten decken.
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Hochzeitsfeier"
                  className="rounded-2xl shadow-xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-primary text-white p-6 rounded-2xl shadow-lg">
                  <Sparkles className="h-8 w-8 mb-2" />
                  <p className="font-bold text-2xl">500+</p>
                  <p className="text-white/80">Hochzeiten vermittelt</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20 bg-secondary">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
                Unsere Werte
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Diese Prinzipien leiten alles, was wir tun
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <Card key={index} className="bg-white border-none shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-8 text-center">
                    <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <value.icon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="font-serif text-xl font-semibold mb-3">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
                Unser Team
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Die Menschen hinter WEDVAYA
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {team.map((member, index) => (
                <Card key={index} className="overflow-hidden border-none shadow-sm hover:shadow-md transition-shadow">
                  <div className="aspect-square">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6 text-center">
                    <h3 className="font-semibold text-lg">{member.name}</h3>
                    <p className="text-primary text-sm mb-2">{member.role}</p>
                    <p className="text-muted-foreground text-sm">{member.bio}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-primary">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
              <div>
                <p className="text-4xl md:text-5xl font-bold mb-1">25+</p>
                <p className="text-white/80">Länder</p>
              </div>
              <div>
                <p className="text-4xl md:text-5xl font-bold mb-1">10K+</p>
                <p className="text-white/80">Glückliche Gäste</p>
              </div>
              <div>
                <p className="text-4xl md:text-5xl font-bold mb-1">500+</p>
                <p className="text-white/80">Hochzeiten</p>
              </div>
              <div>
                <p className="text-4xl md:text-5xl font-bold mb-1">4.9</p>
                <p className="text-white/80">Ø Bewertung</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-secondary">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">
              Werden Sie Teil unserer Geschichte
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Ob als Gastgeber, der seine Kultur teilen möchte, oder als Reisender 
              auf der Suche nach einzigartigen Erlebnissen – bei WEDVAYA sind Sie willkommen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="rounded-full px-8">
                <Link to="/register">Jetzt registrieren</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8">
                <Link to="/weddings">Hochzeiten entdecken</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default AboutPage;
